import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as countryData from "country-telephone-data";
import { BehaviorSubject, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { Geolocation } from "@capacitor/geolocation";
import { ClientPosition, Position, SearchPosition } from "../helpers/types";
import { LocalStorageService } from "./localStorage.service";
import { AppService } from "./app.service";
import { LocationAccuracy } from "@awesome-cordova-plugins/location-accuracy/ngx";
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from "capacitor-native-settings";
import { Capacitor } from "@capacitor/core";
import { error } from "console";

@Injectable({
  providedIn: "root",
})
export class LocationService {
  url = `${environment.apiUrl}`;
  hostUrl = `${environment.imagePath}`;
  authUrl = `${environment.authUrl}`;
  googleMapKey = environment.map_key;

  countries: any[] = [];
  constructor(
    private http: HttpClient,
    private appService: AppService,
    private localStorageService: LocalStorageService,
    private locationAccuracy: LocationAccuracy
  ) { }

  /*==============================
 BehaviorSubject
================================*/

  private userLocationSubject = new BehaviorSubject<Position>({
    label: "",
    coords: {
      latitude: 0,
      longitude: 0,
    },
  });
  private SearchLocationSubject = new BehaviorSubject<
    SearchPosition | null | undefined
  >({
    title: "",
    rue: "",
    country: "",
    pays: "",
    position: [
      {
        lat: 0,
        long: 0,
      },
    ],
  });
  private markerLocationSubject = new BehaviorSubject<Position>({
    label: "",
    coords: {
      latitude: 0,
      longitude: 0,
    },
  });

  userLocation$ = this.userLocationSubject.asObservable();
  SearchLocationSubject$ = this.SearchLocationSubject.asObservable();
  markerLocationSubject$ = this.markerLocationSubject.asObservable();

  setUserLocation(userLocation: Position) {
    this.userLocationSubject.next(userLocation);
  }
  setMarkerLocation(markerLocationSubject: Position) {
    this.markerLocationSubject.next(markerLocationSubject);
  }
  setSearchLocation(SearchLocationSubject: SearchPosition | null) {
    this.SearchLocationSubject.next(SearchLocationSubject);
  }
  removeSearchLocation(SearchLocationSubject: SearchPosition) {
    this.SearchLocationSubject.next(SearchLocationSubject);
  }

  /*==============================
get countries
================================*/

  fetchCountryData(): Observable<any[]> {
    return this.http.get<any[]>("https://restcountries.com/v3.1/all").pipe(
      map((res: any[]) => {
        return res
          .filter(
            (country: any) =>
              country.region === "Europe" || country.region === "Africa"
          )
          .map((country: any) => {
            let dialCode =
              country.callingCodes && country.callingCodes.length > 0
                ? "+" + country.callingCodes[0]
                : "N/A";
            return {
              name: country.name.common,
              code: country.cca2,
              flag: country.flags.png,
            };
          });
      })
    );
  }
  fetchEuropsCountryData(): Observable<any[]> {
    return this.http
      .get<any[]>("https://restcountries.com/v3.1/region/europe")
      .pipe(
        map((res: any[]) => {
          return res.map((country: any) => {
            return {
              name: country.name.common,
              code: country.cca2,
              flag: country.flags.png,
            };
          });
        })
      );
  }
  fetchEuropsCountryFromJson(): Observable<any[]> {
    return this.http
      .get<any[]>("../../assets/settings/europsCountries.json")
      .pipe(
        map((res: any[]) => {
          return res.map((country: any) => {
            return {
              name: country.name.common,
              code: country.cca2,
              flag: country.flags.png,
            };
          });
        })
      );
  }

  getCountriesWithPhoneCodes() {
    const countries = countryData.allCountries.map((res: any) => {
      return {
        name: res.name,
        code: res.iso2,
        dialCode: "+" + res.dialCode,
      };
    });
    return countries;
  }

  getCountriesWithFlag(): Observable<any[]> {
    return new Observable((observer) => {
      this.fetchEuropsCountryFromJson().subscribe((data: any[]) => {
        const array1 = data;
        const array2 = this.getCountriesWithPhoneCodes();
        const mergedArray = array1.map((item) => {
          const country2 = array2.find(
            (c) =>
              c.code.toString().toLowerCase() ===
              item.code.toString().toLowerCase()
          );
          if (country2) {
            item.dialCode = country2.dialCode; // Assign dialCode from array2 to array1
          }
          return item;
        });
        observer.next(mergedArray);
        observer.complete();
      });
    });
  }

  getCurrentLocation(): Promise<Position> {
    return new Promise((resolve, reject) => {
      Geolocation.getCurrentPosition(
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 30000,
        }
      )
        .then((resp) => {
          const coords = {
            coords: {
              latitude: resp.coords.latitude,
              longitude: resp.coords.longitude,
            },
            label: "",
          };
          this.geoCode(resp.coords.latitude, resp.coords.longitude);
          resolve(coords);
        })
        .catch(async (error) => {
          if (error?.message == "location disabled") {
            if (
              Capacitor.getPlatform() == "android" ||
              Capacitor.getPlatform() == "ios"
            ) {
              await this.enableGps();
            }
          }
          if (
            error?.message === "location unavailable" ||
            error?.message === "Location permission was denied"
          ) {
            this.openSettings(true);
          }
        });
    });
  }
  openSettings(app = false) {
    return NativeSettings.open({
      optionAndroid: app
        ? AndroidSettings.ApplicationDetails
        : AndroidSettings.Location,
      optionIOS: app ? IOSSettings.App : IOSSettings.LocationServices,
    });
  }

  async enableGps() {
    const canRequest = await this.locationAccuracy.canRequest();
    if (canRequest) {
      await this.locationAccuracy.request(
        this.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY
      );
      this.getCurrentLocation();
    } else {
      this.openSettings();
    }
  }

  geoCode(latitude: any, longitude: any) {
    return new Promise((resolve, reject) => {
      const config = this.localStorageService.getSettings();
      const map_provider_mobile = config.map_provider_mobile;
      const map_key_mobile = config.map_key_mobile;
      if (map_provider_mobile === "GOOGLEMAP") {
        try {
          this.getPisitionApi(latitude, longitude).subscribe({
            next: (res: any) => {
              const coords = {
                coords: {
                  latitude: latitude,
                  longitude: longitude,
                },
                label: res.addresse,
              };
              this.checkRegion({
                lat: coords.coords.latitude,
                lng: coords.coords.longitude,
              }).subscribe((res: any) => {
                if (res.region_exists) {
                  this.appService.dispatchModal("DETECT_LOCATION_MODAL", false);
                  this.localStorageService.setLocation(coords);
                  resolve(coords);
                }
              });
              resolve(coords);
            },
            error: (error: any) => {
              reject(error);
            },
          });
        } catch (err: any) {
          console.error("err : ", err);
          reject(err);
        }
      }
    });
  }
  getPisitionApi(lat: number, lng: number) {
    const payload = {
      lat: lat,
      long: lng,
    };
    return this.http.post(`${this.url}/latlong2adrs`, payload);
  }
  getGeoCode(latitude: any, longitude: any): Promise<Position> {
    return new Promise((resolve, reject) => {
      try {
        this.getPisitionApi(latitude, longitude).subscribe({
          next: (res: any) => {
            const coords = {
              coords: {
                latitude: latitude,
                longitude: longitude,
              },
              label: res.addresse,
            };
            resolve(coords);
          },
          error: (error: any) => {
            reject(error);
          },
        });
      } catch (err: any) {
        console.error("err : ", err);
      }
    });
  }

  autocomplete(term: string) {
    const params = {
      term: term,
    };

    return this.http.post<any[]>(
      `${this.url}/frontoffice/auto-complete`,
      params
    );
  }
  savePrestatairePosition(data: { long: number; lat: number }) {
    return this.http.post<any[]>(
      `${this.authUrl}/frontoffice/send_prestataire_position`,
      data
    );
  }
  saveClientPosition(data: ClientPosition) {
    return this.http.post<any[]>(`${this.authUrl}/frontoffice/adresses`, data);
  }
  checkRegion(data: { lat: number; lng: number }) {
    return this.http.post<any[]>(
      `${this.url}/check-region-exists`,
      data
    );
  }
  getRegionsList() {
    return this.http.get<any[]>(`${this.authUrl}/frontoffice/areas`);
  }
  calculeDistance(payload: any): Observable<HttpResponse<any>> {
    return this.http.post(`${this.url}/distance`, payload, {
      observe: "response",
    });
  }
  calculeMultiplePointsDistance(payload: any): Observable<HttpResponse<any>> {
    return this.http.post(`${this.url}/distance/points`, payload, {
      observe: "response",
    });
  }
}
